import { useState } from 'react'; 
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import api from '../../../app/api';
import { MfaCodeInput } from './MfaVerificationModal';

const MfaModal = (props) => {
  const { show, setShow, user, handleMfaEnabled, handleMfaType } = props;
  const [authType, setAuthType] = useState('EMAIL');
  const [mfaDetails, setMfaDetails] = useState(null);
  const [mfaEmailCode, setMfaEmailCode] = useState('');

  const showError = (title, message) => {
    props.setErrorTitle(title);
    props.setErrorMessage(message);
    props.setErrorShow(true);
  };

  const handleEnableMfa = () => {
    if (authType === 'EMAIL') {
      handleEmailSetup();
    } else if (authType === 'TOTP') {
      handleAuthenticatorSetup();
    }
  };

  const handleEmailSetup = async () => {
    try {
      await api.get('/auth/mfa/setup/email');
    } catch (error) {
      if (error.response?.status === 409) {
        showError('MFA is already enabled', 'Multi-factor authentication is already enabled for this account.');
      } else {
        showError('Could not send verification email', 'An error occurred while setting up email verification. Please try again.');
      }
    }
  }

  const handleVerifyEmailMfa = async () => {
    try {
      await api.post('/auth/settings/mfa/email/confirm', { code: mfaEmailCode.replace(/\D/g, '') });
      handleMfaEnabled(true);
      handleMfaType('EMAIL');
      setShow(false);
    } catch (error) {
      if (error.response?.status === 400) {
        showError('Invalid verification code', 'Check the code in your email and try again.');
      } else {
        showError('Could not verify the code', 'An error occurred while enabling email verification. Please try again.');
      }
    }
  };

  const handleAuthenticatorSetup = async () => {
    try {
      const response = await api.get('/auth/mfa/setup');
      setMfaDetails(response.data);
    } catch (error) {
      if (error.response?.status === 409) {
        showError('MFA is already enabled', 'Multi-factor authentication is already enabled for this account.');
      } else {
        showError('Could not set up authenticator', 'An error occurred while setting up an authenticator app. Please try again.');
      }
    }
  }

  const handleVerifyMfa = async () => {
    try {
      await api.post('/auth/settings/mfa/totp/confirm', {
        code: (mfaDetails.verificationCode || '').replace(/\D/g, ''),
        pendingSecret: mfaDetails.secret,
      });
      handleMfaEnabled(true);
      handleMfaType('TOTP');
      setShow(false);
    } catch (error) {
      if (error.response?.status === 400) {
        showError('Invalid verification code', 'Check the code in your authenticator app and try again.');
      } else {
        showError('Could not verify the code', 'An error occurred while enabling authenticator verification. Please try again.');
      }
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Multi-factor authentication</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {user.mfaEnabled?
          <>
            <p>MFA is enabled for your account.</p>
            <p>
              <Button variant="danger" onClick={props.handleDisableMfa}>
                Disable MFA
              </Button>
            </p>
          </>
          :
          <>
            <p>MFA is not enabled for your account.</p>
            <p>Choose email or authenticator app, then request a verification code.</p>
            <Form.Check
              inline
              label="Email"
              name="authType"
              type="radio"
              id="auth-type-email"
              checked={authType === 'EMAIL'}
              onChange={() => setAuthType('EMAIL')}
            />
            <Form.Check
              inline
              label="Authenticator App"
              name="authType"
              type="radio"
              id="auth-type-authenticator"
              checked={authType === 'TOTP'}
              onChange={() => setAuthType('TOTP')}
            />
            {authType === 'TOTP' &&
              <div className="mfa-setup-section">
                <p>Scan the QR code with your authenticator app, then enter its verification code.</p>
                <Button variant="primary" onClick={handleEnableMfa}>
                  Set up authenticator
                </Button>
                {mfaDetails &&
                  <>
                    <div className="mfa-qr-code">
                      <img id="qr-code-image" src={mfaDetails.qrCodeUrl} alt="Authenticator setup QR code" />
                    </div>
                    <p>Can't scan? Enter this setup key in your authenticator app:</p>
                    <p><strong>{mfaDetails.secret}</strong></p>
                    <Form onSubmit={event => {
                      event.preventDefault();
                          if ((mfaDetails.verificationCode || '').replace(/\D/g, '').length === 6) handleVerifyMfa();
                    }}>
                    <MfaCodeInput
                      label="Authenticator verification code"
                      value={mfaDetails.verificationCode || ''}
                      onChange={verificationCode => setMfaDetails({ ...mfaDetails, verificationCode })}
                    />
                    <Button className="mt-3" type="submit" variant="primary"
                      disabled={(mfaDetails.verificationCode || '').replace(/\D/g, '').length !== 6}>
                      Verify and enable
                    </Button>
                    </Form>
                  </>}
              </div>}
            {authType === 'EMAIL' &&
              <div className="mfa-setup-section">
                <p>Send a verification code to {user.email} to enable email-based MFA.</p>
                <Button variant="primary" onClick={handleEnableMfa}>
                  Send verification email
                </Button>
                <Form onSubmit={event => {
                  event.preventDefault();
                  if (mfaEmailCode.replace(/\D/g, '').length === 6) handleVerifyEmailMfa();
                }}>
                  <MfaCodeInput
                    label="Email verification code"
                    value={mfaEmailCode}
                    onChange={setMfaEmailCode}
                  />
                  <Button className="mt-3" type="submit" variant="primary"
                    disabled={mfaEmailCode.replace(/\D/g, '').length !== 6}>
                    Verify and enable
                  </Button>
                </Form>
              </div>}
          </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MfaModal;